import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'wouter';
import toast from 'react-hot-toast';
import { useDelayAlertStore, DelayAlert } from '../hooks/useDelayAlertStore';
import { useAuthStore } from '../hooks/useAuth';
import { isOperationsEntitledUser } from '../hooks/useWarehouseEntitlement';

export const SSE_RECONNECT_DELAY_MS = 3000;
export const SSE_MAX_RECONNECT_DELAY_MS = 30000;
export const SSE_RENEWAL_DELAY_MS = 250;

export function DelayNotificationSystem() {
    const { activeAlerts, addAlert, removeAlert } = useDelayAlertStore();
    const [, setLocation] = useLocation();
    const { token, user } = useAuthStore();

    useEffect(() => {
        // Connect whenever a bearer token is present; authoritative role, active-user,
        // warehouse, and trial checks are enforced by the backend on every connection.
        if (!token) return;

        let isDisposed = false;
        let isTerminated = false;
        let isNormalRenewal = false;
        let retryCount = 0;
        let activeController: AbortController | null = null;
        let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

        const handleSsePayload = (rawData: string) => {
            if (!rawData || rawData === ': keep-alive') return;

            try {
                const data = JSON.parse(rawData);

                if (data.type === 'CONNECTED') {
                    retryCount = 0;
                    return;
                }

                // Terminal authorization revocation (expired/invalid JWT, disabled user,
                // removed warehouse access, unauthorized role, or expired/cancelled trial)
                if (data.type === 'STREAM_AUTH_REVOKED') {
                    isTerminated = true;
                    if (activeController && !activeController.signal.aborted) {
                        activeController.abort();
                    }
                    return;
                }

                // Normal 15-minute session lifetime renewal: close cleanly and reconnect
                // immediately without backoff so live notifications continue uninterrupted.
                if (data.type === 'STREAM_RENEW' || data.type === 'STREAM_REAUTH_REQUIRED') {
                    isNormalRenewal = true;
                    retryCount = 0;
                    return;
                }

                // Transient server/database check interruption: stream closes and client
                // reconnects using bounded exponential backoff.
                if (data.type === 'STREAM_RETRY') {
                    isNormalRenewal = false;
                    return;
                }

                if (data.type === 'DELAY_ALERT') {
                    if (!isOperationsEntitledUser(user)) {
                        return;
                    }
                    const newAlert: DelayAlert = {
                        id: data.appointment_id,
                        carrier: data.carrier_name,
                        minutesLate: data.minutes_late,
                        severity: data.severity,
                        receivedAt: Date.now()
                    };

                    addAlert(newAlert);

                    // Trigger toast for immediate attention
                    toast.error(
                        'CRITICAL DELAY: ' + data.carrier_name + ' is ' + data.minutes_late + 'm late!',
                        { duration: Infinity, id: data.appointment_id }
                    );
                }

                if (data.type === 'NEW_REQUEST') {
                    toast.success(
                        `New Appointment Request: ${data.appointment?.carrierName || 'Carrier'}`,
                        {
                            duration: 6000,
                            icon: '📅',
                            id: `new-req-${data.appointment?.id}`
                        }
                    );
                }
            } catch (e) {
                console.error('SSE Parse Error', e);
            }
        };

        const scheduleReconnect = (isImmediateRenewal = false) => {
            if (isDisposed || isTerminated) return;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }

            let delayMs: number;
            if (isImmediateRenewal) {
                delayMs = SSE_RENEWAL_DELAY_MS;
            } else {
                retryCount += 1;
                delayMs = Math.min(
                    SSE_RECONNECT_DELAY_MS * Math.pow(2, Math.max(0, retryCount - 1)),
                    SSE_MAX_RECONNECT_DELAY_MS
                );
            }

            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                void connectStream();
            }, delayMs);
        };

        const connectStream = async () => {
            if (isDisposed || isTerminated) return;

            // Ensure no prior controller remains active before opening a new stream
            if (activeController && !activeController.signal.aborted) {
                activeController.abort();
            }

            isNormalRenewal = false;
            const controller = new AbortController();
            activeController = controller;

            try {
                const response = await fetch('/api/analytics/live-stream', {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'text/event-stream',
                    },
                    cache: 'no-store',
                    signal: controller.signal,
                });

                if (isDisposed || controller.signal.aborted) return;

                // Terminal authorization or trial-expiration responses must not loop reconnects
                if (response.status === 401 || response.status === 403) {
                    isTerminated = true;
                    return;
                }

                // Transient HTTP failures (e.g. 503 AUTH_CHECK_UNAVAILABLE / SUBSCRIPTION_CHECK_UNAVAILABLE)
                // retry with bounded exponential backoff.
                if (!response.ok) {
                    scheduleReconnect(false);
                    return;
                }

                if (!response.body || typeof response.body.getReader !== 'function') {
                    return;
                }

                const reader = response.body.getReader();
                activeReader = reader;
                const decoder = new TextDecoder();
                let buffer = '';

                while (!isDisposed && !isTerminated && !controller.signal.aborted) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

                    let boundaryIdx = buffer.indexOf('\n\n');
                    while (boundaryIdx !== -1) {
                        const rawEventBlock = buffer.slice(0, boundaryIdx);
                        buffer = buffer.slice(boundaryIdx + 2);

                        const lines = rawEventBlock.split('\n');
                        const dataLines: string[] = [];
                        for (const line of lines) {
                            const trimmed = line.trim();
                            if (!trimmed || trimmed.startsWith(':')) {
                                continue;
                            }
                            if (line.startsWith('data:')) {
                                dataLines.push(line.slice(5).trimStart());
                            }
                        }

                        if (dataLines.length > 0) {
                            handleSsePayload(dataLines.join('\n'));
                        }

                        boundaryIdx = buffer.indexOf('\n\n');
                    }
                }
            } catch (err: any) {
                if (isDisposed || isTerminated || controller.signal.aborted || err?.name === 'AbortError') {
                    return;
                }
            } finally {
                if (activeReader) {
                    try {
                        await activeReader.cancel();
                    } catch {
                        // Ignore cancel errors on closed stream
                    }
                    activeReader = null;
                }
            }

            if (!isDisposed && !isTerminated && !controller.signal.aborted) {
                scheduleReconnect(isNormalRenewal);
            }
        };

        void connectStream();

        return () => {
            isDisposed = true;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            if (activeController && !activeController.signal.aborted) {
                activeController.abort();
            }
            if (activeReader) {
                activeReader.cancel().catch(() => {});
                activeReader = null;
            }
        };
    }, [addAlert, token]);

    const handleResolve = (id: string) => {
        toast.dismiss(id);
        setLocation('/dashboard?resolveDelay=' + id);
    };

    return (
        <>
            <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
                {/* Custom Persistent Alert Stack */}
                <AnimatePresence>
                    {activeAlerts.map(alert => (
                        <motion.div
                            key={alert.id}
                            initial={{ opacity: 0, x: 50, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="pointer-events-auto w-96 bg-white dark:bg-slate-800 border-l-4 border-red-500 shadow-2xl rounded-r-lg p-4 flex items-start gap-4"
                        >
                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-400 animate-pulse">
                                ⚠️
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-slate-900 dark:text-slate-100">Delay Threshold Breached</h4>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{alert.carrier}</span> is
                                    <span className="text-red-500 font-bold ml-1">{alert.minutesLate}m late</span>.
                                </p>
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => handleResolve(alert.id)}
                                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wide rounded shadow-sm transition-colors"
                                    >
                                        Resolve Now
                                    </button>
                                    <button
                                        onClick={() => {
                                            removeAlert(alert.id);
                                            toast.dismiss(alert.id);
                                        }}
                                        className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-600 dark:text-slate-300 text-xs font-medium rounded transition-colors"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </>
    );
}
